import { HttpClient, robotoHeaders } from "@/shared/http";
import { PaginatedResponse } from "@/shared/http/response";

import { S3BucketIntegrationRecord } from "./S3BucketIntegrationRecord";
import { ListS3BucketObjectsParams, S3LevelPage } from "./S3ObjectEntry";
import {
  BeginWebhookIntegrationRequest,
  WebhookIntegrationRecord,
} from "./WebhookIntegrationRecord";

interface Options {
  abortSignal?: AbortSignal;
}

export class IntegrationService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async listS3BucketIntegrations(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<S3BucketIntegrationRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/integrations/storage/s3/buckets",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: orgId,
      }),
    });
    return response.json<PaginatedResponse<S3BucketIntegrationRecord>>();
  }

  public async testS3BucketConnection(
    bucketName: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<S3BucketIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/storage/s3/buckets/${encodeURIComponent(bucketName)}/healthcheck`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: orgId,
      }),
    });
    return response.json<S3BucketIntegrationRecord>();
  }

  /**
   * List one level of a bring-your-own bucket, as the given org sees it.
   *
   * Roboto-admin only. The org is an explicit parameter rather than the caller's active org
   * because the same bucket can be registered to several orgs, and an admin browsing one is
   * not necessarily working inside it.
   */
  public async listS3BucketObjects(
    params: ListS3BucketObjectsParams,
    options?: Partial<Options>,
  ): Promise<S3LevelPage> {
    const searchParams = new URLSearchParams({
      org_id: params.orgId,
      kind: params.kind,
    });
    if (params.prefix !== undefined) {
      searchParams.set("prefix", params.prefix);
    }
    if (params.pageToken !== undefined) {
      searchParams.set("page_token", params.pageToken);
    }
    if (params.limit !== undefined) {
      searchParams.set("limit", String(params.limit));
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/integrations/s3/buckets/${encodeURIComponent(params.bucketName)}/objects`,
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<S3LevelPage>();
  }

  /**
   * List every org a bucket name is registered to.
   *
   * Roboto-admin only. Lets an admin holding only a bucket name find which org's registration
   * to browse it under.
   */
  public async getS3BucketBindings(
    bucketName: string,
    options?: Partial<Options>,
  ): Promise<S3BucketIntegrationRecord[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/integrations/s3/buckets/${encodeURIComponent(bucketName)}/bindings`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    const page =
      await response.json<PaginatedResponse<S3BucketIntegrationRecord>>();
    return page.items;
  }

  public async beginWebhookIntegration(
    request: BeginWebhookIntegrationRequest,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/integrations/webhook/begin",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: request.orgId,
      }),
      body: JSON.stringify({
        org_id: request.orgId,
        user_config: request.userConfig,
        webhook_provider: request.webhookProvider,
        webhook_events: request.webhookEvents,
      }),
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async getWebhooksIntegrationByProvider(
    provider: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<WebhookIntegrationRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/provider/${provider}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: orgId,
      }),
    });
    return response.json<PaginatedResponse<WebhookIntegrationRecord>>();
  }

  public async updateWebhookIntegrationSecret(
    webhookEndpointId: string,
    webhookSecret: string,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}/signature_secret`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify({
        webhook_secret: webhookSecret,
      }),
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async getWebhookIntegration(
    webhookEndpointId: string,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async updateWebhookIntegrationApiKey(
    webhookEndpointId: string,
    apiKey: string,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}/api_key`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify({
        webhook_provider_api_key: apiKey,
      }),
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async deleteWebhookIntegration(
    webhookEndpointId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.throwIfError();
  }
}
